import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import SfhPrimaryButton from './SfhPrimaryButton';
import Paragraph from '../../Paragraph/Paragraph';
import SectionLoader from '../../Loader/SectionLoader';
import RowItemElement from '../../Buy/RowItem';
import SelectPaymentOptions from '../../Buy/SelectPaymentOptions';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import SellFromHomeQuantityStepper, {
  MAX_QUANTITY_VALUE,
} from './SellFromHomeQuantityStepper';
import SellFromHomePriceGuaranteeCard from './SellFromHomePriceGuaranteeCard';
import SellFromHomePreAuthDrawer from './SellFromHomePreAuthDrawer';
import SellFromHomeIrsSsnDrawer from './SellFromHomeIrsSsnDrawer';
import SellFromHomeShippingNoticeDrawer from './SellFromHomeShippingNoticeDrawer';
import {
  findDefaultPaymentMethod,
  formatPaymentMethodLabel,
  mapCreditCardsToPaymentMethods,
} from './sellFromHomePaymentHelpers';
import {
  resolveSfhOrderLimit,
  resolveSfhOrderWeightLbs,
  isSfhOrderOverWeightLimit,
  SFH_HIGH_VALUE_SHIPPING_NOTICE_THRESHOLD,
  SFH_COMBINED_LIVE_REQUESTS_THRESHOLD,
  SFH_ORDER_WEIGHT_LIMIT_LBS,
} from './sellFromHomeOrderLimitHelpers';
import {
  resolveSfhVaultSymbol,
  SFH_VAULT_LOCATION,
} from './sellFromHomeVaultHelpers';
import { ADD_ADDRESS_WIZARD } from '../../../constants/sessionStorage';
import { removeFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import {
  fetchProductSymbolPrices,
  fetchProductTiers,
} from '../../../store/actions/products/productActions';
import { fetchCreditCards } from '../../../store/actions/creditCard/creditCardActions';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import { selectCreditCards } from '../../../store/selectors/creditCardSelectors';
import {
  getCurrentAccountUid,
  getCurrentAccount,
  selectAccountAddresses,
  selectDefaultMailingAddress,
  selectHasFullTaxIdOnFile,
  selectPrimaryAccountHolderDisplayName,
} from '../../../store/selectors/accountSelectors';
import {
  fetchAccountAddresses,
  fetchAccountUsers,
} from '../../../store/actions/account/accountActions';
import {
  getSfhIrsReportingRequired,
  getSfhTotalRequestsAmount,
} from '../../../store/actions/orders/orderActions';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import {
  PRODUCT_SYMBOL_PRICES_LOADING,
  PRODUCT_TIERS_LOADING,
} from '../../../store/actions/products/productActionConstants';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as SfhCreditCard } from '../../../assets/images/svg/sfh-credit-card.svg';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { accountTypeKeys } from '../../../util/enum/api/accountTypes';
import { GS_IRS_LINK_UPDATED } from '../../../util/constants';
import { buildSfhIrsDrawerCopy } from '../../../constants/sfhIrsCopy';
import { resolveSfhSellTaxComplianceGate } from '../../../services/sfhSellTaxComplianceGate';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_QTY_LEARN_MORE_CLICKED,
  SFH_VALUE_LIMIT_BREACHED,
  SFH_WEIGHT_LIMIT_BREACHED,
  SFH_CHANGE_CC_CLICKED,
  SFH_ADD_CC_CLICKED,
  SFH_CONTINUE_TO_SHIPPING_CLICKED,
} from '../../../constants/gtmEvents';

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ProductTitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  color: ${themeColors.colorTextPrimary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const UnitPrice = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  text-align: right;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 130px;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const EmptyHint = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  background: ${themeColors.colorPrimaryLight};
  border-radius: 4px;
  border: 1px solid ${themeColors.colorCardBorder};
  padding: 12px 16px;
  font-weight: 500;
`;

const UnitsTotal = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  background: ${themeColors.colorPrimaryLight};
  border-radius: 4px;
  border: 1px solid ${themeColors.colorCardBorder};
  padding: 12px 16px;

  > b {
    color: ${themeColors.colorTextPrimary};
    font-weight: 600;
  }

  > span {
    color: ${themeColors.colorBlack};
    font-weight: 600;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 64px;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: 0;
  }
`;

const OverLimitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
`;

// Matches SFH modal notice/warning boxes (WhatHappensNext / CancelOrder NoticeBox):
// left-aligned amber icon + wrapping copy, 12x16 padding, 20px icon.
const WarningPill = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 6px;
  padding: 12px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};

  > span {
    color: inherit;
  }

  > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const PhoneLink = styled.a`
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 16px;
`;

// Shown above the CTA only when the payment row is absent (quantity 0).
// Once the payment row renders, its own top border serves as the divider.
const FooterDivider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0 -16px 16px;
`;

const Footer = styled.div`
  /* Font size/weight/line-height are enforced by SfhPrimaryButton; this only
     owns layout and the screen-specific label color. */
  > button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
    color: ${themeColors.colorGrey};
  }

  /* Figma disabled state: solid grey (colorGrey3 #ADB1C7) at full opacity,
     not the default translucent primary. :hover beats the primary variant's
     own :disabled:hover rule. */
  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const PRICE_POLL_INTERVAL_MS = 30_000;

const SellFromHomeQuantity = ({
  selectedHit,
  quantity,
  setQuantity,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  handleNext,
  handleClose,
  shippingNoticeAcknowledged,
  setShippingNoticeAcknowledged,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sfhGtmEvent } = useSfhGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const hasFullTaxIdOnFile = useSelector(selectHasFullTaxIdOnFile);
  const primaryHolderDisplayName = useSelector(
    selectPrimaryAccountHolderDisplayName,
  );
  const productBySymbol = useSelector(getProductBySymbol);
  const creditCards = useSelector(selectCreditCards);
  const isLoadingPrice = useSelector(
    selectIsLoadingByActionTypes([
      PRODUCT_SYMBOL_PRICES_LOADING,
      PRODUCT_TIERS_LOADING,
    ]),
  );
  const [isPreAuthOpen, setIsPreAuthOpen] = useState(false);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isIrsDrawerOpen, setIsIrsDrawerOpen] = useState(false);
  const [isShippingNoticeOpen, setIsShippingNoticeOpen] = useState(false);
  const [dailyLimitNoticeRequired, setDailyLimitNoticeRequired] = useState(
    false,
  );
  const [tierPricePerUnit, setTierPricePerUnit] = useState(null);
  const [hasTouchedQuantity, setHasTouchedQuantity] = useState(false);
  const [isCheckingIrsReporting, setIsCheckingIrsReporting] = useState(false);
  const [isCheckingCombinedValue, setIsCheckingCombinedValue] = useState(false);
  const [irsReportingError, setIrsReportingError] = useState('');
  const [plusClickCount, setPlusClickCount] = useState(0);
  const [minusClickCount, setMinusClickCount] = useState(0);

  useEffect(() => {
    if (quantity > 0 && !hasTouchedQuantity) setHasTouchedQuantity(true);
  }, [quantity, hasTouchedQuantity]);

  const productCode = selectedHit?.Code;
  const vaultSymbol = useMemo(
    () => resolveSfhVaultSymbol(selectedHit, account),
    [selectedHit, account],
  );

  useEffect(() => {
    if (!accountUid || !vaultSymbol || hasTouchedQuantity) return undefined;
    const dispatchFetch = () => {
      dispatch(
        fetchProductSymbolPrices({
          accountUid,
          symbol: vaultSymbol,
          side: 'sell',
        }),
      );
    };
    dispatchFetch();
    const intervalId = setInterval(dispatchFetch, PRICE_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [accountUid, vaultSymbol, hasTouchedQuantity, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchCreditCards({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountUsers({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  const unitPrice = hasTouchedQuantity
    ? Number(tierPricePerUnit) || 0
    : Number(productBySymbol?.ProductPrice) || 0;
  const hasUnitPrice = unitPrice > 0;

  const creditCardPaymentMethods = useMemo(
    () => mapCreditCardsToPaymentMethods(creditCards),
    [creditCards],
  );

  const defaultPaymentMethod = useMemo(
    () => findDefaultPaymentMethod(creditCardPaymentMethods),
    [creditCardPaymentMethods],
  );

  useEffect(() => {
    if (!selectedPaymentMethod && defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod, setSelectedPaymentMethod]);

  const activePaymentMethod = selectedPaymentMethod || defaultPaymentMethod;

  useEffect(() => {
    // Freeze the price refresh once the user commits via Continue to Shipping
    // (IRS reporting check in flight) or the IRS drawer opens — the price they
    // agreed to is locked. Skip the reset + poll until they return here.
    if (
      isIrsDrawerOpen ||
      isCheckingIrsReporting ||
      isCheckingCombinedValue ||
      isShippingNoticeOpen
    ) {
      return undefined;
    }
    setTierPricePerUnit(null);
    if (!accountUid || !vaultSymbol || !hasTouchedQuantity) return undefined;
    const dispatchFetch = () => {
      dispatch(
        fetchProductTiers({
          accountUid,
          symbol: vaultSymbol,
          side: 'Sell',
          location: SFH_VAULT_LOCATION,
          trueTiers: true,
          onSuccess: (data) => {
            const firstTierUnitPrice = Number(data?.Tiers?.[0]?.PricePerUnit);
            setTierPricePerUnit(
              Number.isFinite(firstTierUnitPrice) ? firstTierUnitPrice : null,
            );
          },
        }),
      );
    };
    dispatchFetch();
    const intervalId = setInterval(dispatchFetch, PRICE_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    accountUid,
    vaultSymbol,
    hasTouchedQuantity,
    isIrsDrawerOpen,
    isCheckingIrsReporting,
    isCheckingCombinedValue,
    isShippingNoticeOpen,
    dispatch,
  ]);

  const orderLimit = useMemo(() => resolveSfhOrderLimit(selectedHit), [
    selectedHit,
  ]);

  const backendOrderTotal = hasUnitPrice ? unitPrice * quantity : 0;
  const hasBackendOrderTotal = quantity > 0 && backendOrderTotal > 0;
  const isOverValueLimit = hasUnitPrice && backendOrderTotal > orderLimit;
  const isOverWeightLimit = isSfhOrderOverWeightLimit(selectedHit, quantity);
  const isOverLimit = isOverValueLimit || isOverWeightLimit;
  const supportPhone = t('sellWizard.sfhWizard.quantity.orderLimitPhone');
  const supportPhoneTel = t('sellWizard.sfhWizard.quantity.orderLimitPhoneTel');
  const sku = vaultSymbol || productCode || '';

  const isIncrementDisabled = useMemo(() => {
    if (quantity >= MAX_QUANTITY_VALUE) return true;
    if (hasUnitPrice && unitPrice * quantity >= orderLimit) return true;
    // Use `>` (not `>=`) so users can step one unit past the limit (e.g. 608 →
    // 609 for 1oz SKUs), which surfaces the warning; then further + is blocked.
    if (
      resolveSfhOrderWeightLbs(selectedHit, quantity) >
      SFH_ORDER_WEIGHT_LIMIT_LBS
    ) {
      return true;
    }
    return false;
  }, [quantity, hasUnitPrice, unitPrice, orderLimit, selectedHit]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQuantity((q) => {
          if (q >= MAX_QUANTITY_VALUE) return q;
          if (hasUnitPrice && unitPrice * q >= orderLimit) return q;
          if (
            resolveSfhOrderWeightLbs(selectedHit, q) >
            SFH_ORDER_WEIGHT_LIMIT_LBS
          ) {
            return q;
          }
          return q + 1;
        });
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQuantity((q) => Math.max(0, q - 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setQuantity, hasUnitPrice, unitPrice, orderLimit, selectedHit]);

  useEffect(() => {
    if (isOverValueLimit) {
      sfhGtmEvent(SFH_VALUE_LIMIT_BREACHED, {
        sku,
        entered_quantity: quantity,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverValueLimit]);

  useEffect(() => {
    if (isOverWeightLimit) {
      sfhGtmEvent(SFH_WEIGHT_LIMIT_BREACHED, {
        sku,
        entered_quantity: quantity,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverWeightLimit]);

  const accountType = parseEnumType(accountTypeKeys, account?.AccountType);

  const irdCopy = useMemo(
    () =>
      buildSfhIrsDrawerCopy({
        t,
        accountType,
        primaryHolderDisplayName,
        learnMoreHref: GS_IRS_LINK_UPDATED,
      }),
    [t, accountType, primaryHolderDisplayName],
  );

  const addressUid =
    defaultMailingAddress?.AddressUid ||
    accountAddresses?.find((a) => a?.IsVerified)?.AddressUid ||
    accountAddresses?.[0]?.AddressUid;

  const requiresHighValueShippingNotice =
    backendOrderTotal > SFH_HIGH_VALUE_SHIPPING_NOTICE_THRESHOLD;

  const handleTotalRequestsAmountSuccess = (result) => {
    const existingLiveRequestsTotal =
      Number(result?.TotalSfhRequestsAmount) || 0;
    const requiresDailyLimitNotice =
      backendOrderTotal + existingLiveRequestsTotal >
      SFH_COMBINED_LIVE_REQUESTS_THRESHOLD;

    if (requiresHighValueShippingNotice || requiresDailyLimitNotice) {
      setDailyLimitNoticeRequired(requiresDailyLimitNotice);
      setIsShippingNoticeOpen(true);
      return;
    }
    handleNext();
  };

  const handleTotalRequestsAmountError = (error) => {
    const errorCode = getErrorCodeFromAPIResponse(error);
    setIrsReportingError(
      errorCode ? t(`apiErrors.${errorCode}`) : t('common.error'),
    );
  };

  const proceedAfterCompliance = () => {
    setIsCheckingCombinedValue(true);
    dispatch(
      getSfhTotalRequestsAmount({
        accountUid,
        isLoading: setIsCheckingCombinedValue,
        onSuccess: handleTotalRequestsAmountSuccess,
        onError: handleTotalRequestsAmountError,
      }),
    );
  };

  const handleIrsReportingSuccess = (result) => {
    const { requiresSsnUi } = resolveSfhSellTaxComplianceGate({
      irsReportingRequired: result?.IRSReportingRequired,
      hasFullTaxIdOnFile,
    });

    if (requiresSsnUi) {
      setIsIrsDrawerOpen(true);
      return;
    }
    proceedAfterCompliance();
  };

  const handleIrsReportingError = (error) => {
    const errorCode = getErrorCodeFromAPIResponse(error);
    setIrsReportingError(
      errorCode ? t(`apiErrors.${errorCode}`) : t('common.error'),
    );
  };

  const handleContinue = () => {
    if (
      !productCode ||
      !accountUid ||
      !vaultSymbol ||
      !handleNext ||
      !activePaymentMethod ||
      !addressUid
    ) {
      return;
    }

    sfhGtmEvent(SFH_CONTINUE_TO_SHIPPING_CLICKED, {
      sku,
      quantity,
      unit_price: unitPrice,
      total_payout: backendOrderTotal,
      no_of_plus_clicks: plusClickCount,
      no_of_minus_clicks: minusClickCount,
    });

    setIrsReportingError('');
    setIsCheckingIrsReporting(true);
    dispatch(
      getSfhIrsReportingRequired({
        accountUid,
        symbolCode: vaultSymbol,
        quantity,
        isLoading: setIsCheckingIrsReporting,
        onSuccess: handleIrsReportingSuccess,
        onError: handleIrsReportingError,
      }),
    );
  };

  const handleIrsDrawerSuccess = () => {
    setIsIrsDrawerOpen(false);
    proceedAfterCompliance();
  };

  const handleShippingNoticeContinue = () => {
    setIsShippingNoticeOpen(false);
    handleNext();
  };

  const handleIrsLockedOut = () => {
    setIsIrsDrawerOpen(false);
    if (typeof handleClose === 'function') {
      handleClose();
    }
  };

  return (
    <SectionLoader
      isLoading={
        isLoadingPrice || isCheckingIrsReporting || isCheckingCombinedValue
      }
    >
      <ModalBody>
        <Header>
          <HeaderLeft>
            <ProductTitle data-cy="text-sfh-product-title">
              {selectedHit?.Caption}
            </ProductTitle>
            <ProductWeight>{selectedHit?.WeightCaption}</ProductWeight>
          </HeaderLeft>
          <UnitPrice data-cy="text-sfh-unit-price">
            {hasUnitPrice ? formatMoneyNumeral(unitPrice) : ''}
          </UnitPrice>
        </Header>

        <SellFromHomeQuantityStepper
          value={quantity}
          onChange={setQuantity}
          incrementDisabled={isIncrementDisabled}
          sku={sku}
          onIncrementClick={() => setPlusClickCount((c) => c + 1)}
          onDecrementClick={() => setMinusClickCount((c) => c + 1)}
        />

        <Body>
          {quantity === 0 ? (
            <EmptyHint data-cy="text-sfh-select-quantity">
              {t('sellWizard.sfhWizard.quantity.selectQuantity')}
            </EmptyHint>
          ) : (
            <>
              <UnitsTotal data-cy="text-sfh-units-total">
                {t('sellWizard.sfhWizard.quantity.unitsLabel', {
                  count: quantity,
                })}
                {hasBackendOrderTotal && (
                  <>
                    {' = '}
                    <span>{formatMoneyNumeral(backendOrderTotal)}</span>
                  </>
                )}
              </UnitsTotal>
              {!isOverLimit && hasUnitPrice && (
                <SellFromHomePriceGuaranteeCard
                  price={unitPrice}
                  onLearnMore={() => {
                    sfhGtmEvent(SFH_QTY_LEARN_MORE_CLICKED, { sku });
                    setIsPreAuthOpen(true);
                  }}
                />
              )}
            </>
          )}
        </Body>

        <Spacer />

        {quantity > 0 && isOverLimit && (
          <OverLimitSection>
            <WarningPill data-cy="text-sfh-order-limit-warning">
              <AmberAlertTriangle />
              <span>
                <Trans
                  i18nKey="sellWizard.sfhWizard.quantity.orderLimitWarning"
                  values={{
                    limit: formatMoneyNumeral(orderLimit, '$0,0'),
                    phone: supportPhone,
                  }}
                  components={{
                    phoneLink: <PhoneLink href={`tel:${supportPhoneTel}`} />,
                  }}
                />
              </span>
            </WarningPill>
          </OverLimitSection>
        )}

        {quantity > 0 && activePaymentMethod && (
          <RowItemElement
            label={t('sellWizard.sfhWizard.quantity.payWith')}
            value={
              <>
                <SfhCreditCard />
                {formatPaymentMethodLabel(activePaymentMethod, t)}
              </>
            }
            clickLabel={t('sellWizard.sfhWizard.quantity.change')}
            onClick={() => {
              sfhGtmEvent(SFH_CHANGE_CC_CLICKED, { sku, page: 'quantity' });
              setIsPaymentDrawerOpen(true);
            }}
          />
        )}

        {quantity > 0 && !activePaymentMethod && (
          <RowItemElement
            label={t('sellWizard.sfhWizard.quantity.payWith')}
            value={t('sellWizard.sfhWizard.quantity.addCreditCard')}
            onClickValue={() => {
              sfhGtmEvent(SFH_ADD_CC_CLICKED, { sku, page: 'quantity' });
              setIsAddPaymentMethodOpen(true);
            }}
            isNotButton
          />
        )}

        {irsReportingError && (
          <Paragraph isError fontSize={14} marginTop={12}>
            {irsReportingError}
          </Paragraph>
        )}

        <Footer>
          {quantity === 0 && <FooterDivider />}
          <SfhPrimaryButton
            disabled={
              quantity === 0 ||
              isOverLimit ||
              !activePaymentMethod ||
              !addressUid ||
              !hasUnitPrice ||
              !hasBackendOrderTotal ||
              isCheckingIrsReporting ||
              isCheckingCombinedValue
            }
            onClick={handleContinue}
            data-cy="button-sfh-continue-shipping"
          >
            {t('sellWizard.sfhWizard.quantity.continueToShipping')}
          </SfhPrimaryButton>
        </Footer>
      </ModalBody>
      <SellFromHomePreAuthDrawer
        isOpen={isPreAuthOpen}
        onClose={() => setIsPreAuthOpen(false)}
      />
      <SelectPaymentOptions
        open={isPaymentDrawerOpen}
        setOpen={setIsPaymentDrawerOpen}
        paymentMethods={creditCardPaymentMethods}
        selectedPaymentMethod={activePaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        accountUid={accountUid}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        wizardData={{}}
        setWizardData={() => {}}
        setSuccessToast={() => {}}
        creditCardOnly
        onAddPaymentMethodClick={() =>
          sfhGtmEvent(SFH_ADD_CC_CLICKED, { sku, page: 'quantity' })
        }
      />
      {isAddAddressOpen && (
        <AddDeliveryAddressModal
          isOpen={isAddAddressOpen}
          onClose={() => {
            removeFromSessionStorage(ADD_ADDRESS_WIZARD);
            setIsAddAddressOpen(false);
          }}
          title={t('buyWizard.addYourDeliveryAddress')}
          page="address"
          avoidClearingStorage
          isAddAddressOpen={isAddAddressOpen}
          setIsAddAddressOpen={setIsAddAddressOpen}
        />
      )}
      <SellFromHomeIrsSsnDrawer
        isOpen={isIrsDrawerOpen}
        onClose={() => setIsIrsDrawerOpen(false)}
        onSuccess={handleIrsDrawerSuccess}
        onLockedOut={handleIrsLockedOut}
        accountUid={accountUid}
        copy={irdCopy}
      />
      <SellFromHomeShippingNoticeDrawer
        isOpen={isShippingNoticeOpen}
        onClose={() => setIsShippingNoticeOpen(false)}
        acknowledged={shippingNoticeAcknowledged}
        onAcknowledgedChange={setShippingNoticeAcknowledged}
        onContinue={handleShippingNoticeContinue}
        showHighValueNotice={requiresHighValueShippingNotice}
        showDailyLimitNotice={dailyLimitNoticeRequired}
      />
    </SectionLoader>
  );
};

SellFromHomeQuantity.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    MetalType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  quantity: PropTypes.number,
  setQuantity: PropTypes.func,
  selectedPaymentMethod: PropTypes.shape({}),
  setSelectedPaymentMethod: PropTypes.func,
  handleNext: PropTypes.func,
  handleClose: PropTypes.func,
  shippingNoticeAcknowledged: PropTypes.bool,
  setShippingNoticeAcknowledged: PropTypes.func,
};

export default SellFromHomeQuantity;
